import { Table } from "antd"
import React, { useState, useEffect, useRef, useMemo } from "react"
import PropTypes from "prop-types"
import { Icon } from "ui/Icon"
import { columns } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricingTable/Columns"
import { EditPricesForm } from "./EditPricesForm"
import styles from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricingTable/PricingTable.module.css"

/**
 * Pricing table for the new wizard.
 * Reads from `prices` prop (localPrices from SchedulingStep) instead of Redux.
 * Calls onPriceSave / onPriceDelete callbacks instead of dispatching API actions.
 */
export const LocalPricingTable = ({
  prices,
  draftPrices,
  setDraftPrices,
  addNewPrice,
  setPricingType,
  isFlatRate,
  pricingType,
  expandedRowData,
  setExpandedRowData,
  form,
  onPriceSave,
  onPriceDelete
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const lastExpandedId = useRef(null)

  // Combined: locally-saved prices + open draft rows
  const combinedData = [...prices, ...draftPrices]
  const filteredData = pricingType
    ? combinedData.filter((item) => item.pricing_type === pricingType || !item.pricing_type)
    : combinedData

  const closeEditForm = () => {
    setExpandedRowKeys([])
    setExpandedRowData({})
  }

  // Auto-expand the newest draft row
  useEffect(() => {
    if (draftPrices.length > 0) {
      const newestRow = draftPrices[draftPrices.length - 1]
      if (newestRow.id !== lastExpandedId.current) {
        setExpandedRowKeys([newestRow.id])
        if (newestRow.pricing_type) setPricingType(newestRow.pricing_type)
        lastExpandedId.current = newestRow.id
      }
    }
  }, [draftPrices, prices, setPricingType])

  const filteredColumns = useMemo(() => {
    if (pricingType === "charge_per_person") {
      return columns.filter((col) => col.key !== "flat_rate")
    }
    return columns
  }, [pricingType])

  // Draft prices use string UUID as `id`; locally-saved new prices use `tempId`
  const rowKey = (record) => record.id ?? record.tempId

  return (
    <div className={styles.responsiveTableContainer}>
      <Table
        dataSource={filteredData}
        columns={filteredColumns}
        expandable={{
          expandedRowKeys,
          expandedRowRender: (record) => (
            <EditPricesForm
              record={record}
              key={record.id ?? record.tempId}
              expandedRowData={expandedRowData}
              isFlatRate={isFlatRate}
              closeEditForm={closeEditForm}
              setDraftPrices={setDraftPrices}
              addNewPrice={addNewPrice}
              form={form}
              onPriceSave={onPriceSave}
              onPriceDelete={onPriceDelete}
            />
          ),
          rowExpandable: () => true,
          expandedRowClassName: () => styles.expandedRow,
          expandIcon: ({ expanded, onExpand, record }) => (
            <Icon
              type="feather"
              name={expanded ? "ChevronUp" : "ChevronDown"}
              color="currentColor"
              width={20}
              height={20}
              onClick={(e) => onExpand(record, e)}
            />
          ),
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [rowKey(record)] : [])
            setExpandedRowData(expanded ? record : null)
            if (record.pricing_type) setPricingType(record.pricing_type)
          }
        }}
        pagination={false}
        rowHoverable={false}
        size="middle"
        className={styles.customTable}
        rowClassName={styles.customTableRow}
        rowKey={rowKey}
      />
    </div>
  )
}

LocalPricingTable.propTypes = {
  prices: PropTypes.arrayOf(PropTypes.object).isRequired,
  draftPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  setPricingType: PropTypes.func.isRequired,
  isFlatRate: PropTypes.bool,
  pricingType: PropTypes.string,
  form: PropTypes.object.isRequired,
  expandedRowData: PropTypes.object,
  setExpandedRowData: PropTypes.func,
  onPriceSave: PropTypes.func.isRequired,
  onPriceDelete: PropTypes.func.isRequired
}
